'use client';

import { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from '@/lib/toast';
import { handleApiError } from '@/lib/error-handler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScopeTypeSelector } from '@/components/iam/ScopeTypeSelector';
import {
  listDataScopes,
  createDataScope,
  updateDataScope,
  deleteDataScope,
  type DataScope,
  type DataScopeType,
} from '@/services/api/iam-admin';

export default function DataScopesPage() {
  const { t } = useTranslation();
  const [list, setList] = useState<DataScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<DataScope | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      setList(await listDataScopes());
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.loadFailed });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const onDelete = async (s: DataScope) => {
    if (s.isBuiltIn) {
      toast.error(t.iamAdmin.dataScopes.builtInImmutable);
      return;
    }
    if (!confirm(t.iamAdmin.dataScopes.confirmDelete.replace('{code}', s.code))) return;
    try {
      await deleteDataScope(s.id);
      toast.success(t.iamAdmin.common.deleted);
      reload();
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.deleteFailed });
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t.iamAdmin.dataScopes.title}
          </h1>
          <p className="text-sm text-gray-500">{t.iamAdmin.dataScopes.subtitle}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          {t.iamAdmin.dataScopes.newButton}
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.iamAdmin.dataScopes.columns.code}</TableHead>
              <TableHead>{t.iamAdmin.dataScopes.columns.name}</TableHead>
              <TableHead>{t.iamAdmin.dataScopes.columns.scopeType}</TableHead>
              <TableHead>{t.iamAdmin.dataScopes.columns.builtIn}</TableHead>
              <TableHead className="text-right">{t.iamAdmin.dataScopes.columns.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">{t.iamAdmin.common.loading}</TableCell></TableRow>
            )}
            {!loading && list.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-8">{t.iamAdmin.common.empty}</TableCell></TableRow>
            )}
            {list.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-mono text-sm">{s.code}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell><span className="font-mono text-xs">{s.scopeType}</span></TableCell>
                <TableCell>
                  {s.isBuiltIn ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{t.iamAdmin.dataScopes.builtIn}</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">{t.iamAdmin.dataScopes.custom}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(s)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(s)}
                    disabled={s.isBuiltIn}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {createOpen && (
        <DataScopeDialog
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); reload(); }}
        />
      )}
      {editing && (
        <DataScopeDialog
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function DataScopeDialog({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial?: DataScope;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [scopeType, setScopeType] = useState<DataScopeType | undefined>(initial?.scopeType);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim() || !scopeType || (mode === 'create' && !code.trim())) {
      toast.error(t.iamAdmin.common.fillRequired);
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'create') {
        await createDataScope({ code: code.trim(), name: name.trim(), scopeType });
      } else if (initial) {
        await updateDataScope(initial.id, { name: name.trim(), scopeType });
      }
      toast.success(t.iamAdmin.common.saved);
      onSaved();
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.saveFailed });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t.iamAdmin.dataScopes.newButton : t.iamAdmin.dataScopes.editTitle}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t.iamAdmin.dataScopes.columns.code}</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={mode === 'edit'}
              placeholder="MY_CUSTOM_SCOPE"
            />
            <p className="mt-1 text-xs text-gray-500">{t.iamAdmin.dataScopes.codeHint}</p>
          </div>
          <div>
            <Label>{t.iamAdmin.dataScopes.columns.name}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label>{t.iamAdmin.dataScopes.columns.scopeType}</Label>
            <ScopeTypeSelector value={scopeType} onChange={setScopeType} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.iamAdmin.common.cancel}</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? t.iamAdmin.common.submitting : t.iamAdmin.common.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
